import { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useAuth0 } from "../../components/auth/DevAuth0Provider";
import { useTheme } from "../../contexts/ThemeContext";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { useNavigation } from "../../hooks/useNavigation";

const UserSettings = () => {
    const user = useAuthUser();
    const { logout } = useAuth0();
    const { theme, toggleTheme, useSystemDefault, setSystemDefaultEnabled } = useTheme();
    const { preferences, updatePreference, getDefaultDateRangeDays, setDefaultDateRangeDays } = useUserPreferences();
    const [activeTab, setActiveTab] = useState('profile');

    // Get available navigation options for current user
    const adminNavigation = useNavigation('admin');
    const clientNavigation = useNavigation('client');

    const [animationStates, setAnimationStates] = useState({
        header: false,
        tabs: false,
        content: false,
    });

    // Get available default page options based on user role
    const getDefaultPageOptions = () => {
        const isAdmin = user?.isHeliosAdmin;
        
        if (isAdmin) {
            // For admin users, show admin pages
            const adminOptions = [
                { value: 'admin-dashboard', label: 'Admin Dashboard' },
                { value: 'admin-clients', label: 'Clients' },
                { value: 'admin-activity', label: 'Activity' },
                { value: 'admin-security', label: 'Security' },
                { value: 'alerts', label: 'Alerts' },
                { value: 'tickets', label: 'Tickets' },
                { value: 'chat', label: 'AI Chat' },
                { value: 'settings', label: 'Settings' }
            ];
            return adminOptions;
        } else {
            // For client users, show their available navigation pages
            const clientOptions = clientNavigation.navLinks.map(link => {
                // Extract the page path from the full link path
                const pathParts = link.path.split('/');
                const pagePath = pathParts[pathParts.length - 1];
                
                return {
                    value: pagePath,
                    label: link.title
                };
            });
            
            // Add common pages that might not be in navigation
            const commonOptions = [
                { value: 'settings', label: 'Settings' },
                { value: 'help', label: 'Help' }
            ];
            
            // Merge and deduplicate
            const allOptions = [...clientOptions];
            commonOptions.forEach(commonOption => {
                if (!allOptions.find(opt => opt.value === commonOption.value)) {
                    allOptions.push(commonOption);
                }
            });
            
            return allOptions;
        }
    };

    const defaultPageOptions = getDefaultPageOptions();

    // Get the display label for the current default page
    const getCurrentDefaultLabel = () => {
        if (!preferences.defaultPage) return null;
        
        const currentOption = defaultPageOptions.find(option => option.value === preferences.defaultPage);
        return currentOption ? currentOption.label : preferences.defaultPage;
    };

    const currentDefaultLabel = getCurrentDefaultLabel();

    const handleDefaultPageChange = (event) => {
        const selectedPage = event.target.value;
        updatePreference('defaultPage', selectedPage);
    };

    const handleDefaultDateRangeDaysChange = (event) => {
        const days = event.target.value;
        setDefaultDateRangeDays(days);
    };



    const tabs = [
        { id: 'profile', label: 'Profile', icon: 'mdi:account' },
        { id: 'preferences', label: 'Preferences', icon: 'mdi:cog' },
        { id: 'security', label: 'Security', icon: 'mdi:shield-account' },
        { id: 'notifications', label: 'Notifications', icon: 'mdi:bell' }
    ];

    const handleLogout = () => {
        logout({ returnTo: window.location.origin, logoutReason: 'manual' });
    };

    const handlePasswordReset = async () => {
        try {
            // Use Auth0's password reset API
            const response = await fetch(`https://${process.env.REACT_APP_AUTH0_DOMAIN}/dbconnections/change_password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
                    email: user.email,
                    connection: 'Username-Password-Authentication'
                })
            });

            if (response.ok) {
                alert('Password reset email sent! Check your inbox for instructions.');
            } else {
                throw new Error('Failed to send password reset email');
            }
        } catch (error) {
            console.error('Password reset error:', error);
            alert('Failed to send password reset email. Please try again or contact support.');
        }
    };



    const  renderTabContent = () => {
        switch (activeTab) {
            case 'profile':
                return (
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-4">
                            <h3 className="subheader-textbox gap-1">
                                {/* <Icon icon="mdi:account" className="responsive-icon-sm text-primary" /> */}
                                Profile
                            </h3>
                            <div className="flex flex-row gap-4 items-center">
                                <img 
                                    src={user.picture} 
                                    className="w-20 h-20 rounded-md" 
                                    alt="Profile" 
                                />
                                <div className="flex flex-col gap-2">
                                    <button className="px-4 py-2 button-container rounded-md text-sm">
                                        Change Photo
                                    </button>
                                    <button className="px-4 py-2 button-container rounded-md text-sm text-error">
                                        Remove Photo
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-2">
                                <label className="text-base font-semibold">Full Name</label>
                                <input 
                                    type="text" 
                                    value={user.name} 
                                    className="p-3 text-font-primary input-textbox bg-border rounded-md" 
                                    readOnly
                                    disabled
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-base font-semibold">Email</label>
                                <input 
                                    type="email" 
                                    value={user.email} 
                                    className="p-3 text-font-primary input-textbox bg-border rounded-md" 
                                    readOnly 
                                    disabled
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-base font-semibold">Role</label>
                                <input 
                                    type="text" 
                                    value={user.role === 'helios_admin' ? 'Helios Admin' : 'Tenant Admin'} 
                                    className="p-3 text-font-primary input-textbox bg-border rounded-md" 
                                    readOnly 
                                    disabled
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-base font-semibold">Organization</label>
                                <input 
                                    type="text" 
                                    value={user.organization || 'Helios Core'} 
                                    className="p-3 text-font-primary input-textbox bg-border rounded-md" 
                                    readOnly 
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                );
            
            case 'preferences':
                return (
                    <div className="flex flex-col gap-3">
                        <h3 className="subheader-textbox gap-1">
                            {/* <Icon icon="mdi:cog" className="responsive-icon-sm text-primary" /> */}
                            Preferences
                        </h3>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-row justify-start items-center justify-between gap-4">
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-base">Theme</span>
                                    <span className="text-sm text-font-secondary">Switch between light and dark themes or use system default</span>
                                </div>
                                
                                <div className="flex flex-col md:flex-row items-end md:items-center gap-2">
                                    <div className={`flex items-center p-1 transition-opacity ${
                                        useSystemDefault ? 'opacity-50 pointer-events-none' : 'opacity-100'
                                    }`}>
                                        <button
                                            onClick={() => { if (theme === 'dark' && !useSystemDefault) toggleTheme(); }}
                                            disabled={useSystemDefault}
                                            className={`px-4 py-1.5 text-sm transition-all duration-300 flex items-center gap-1 rounded-md ${
                                                theme === 'light' && !useSystemDefault ? 'button-container rounded-md' : 'bg-transparent text-font-secondary'
                                            }`}
                                        >
                                            <Icon icon="mdi:weather-sunny" className={`text-lg ${theme === 'light' && !useSystemDefault ? 'text-primary' : 'text-font-secondary'}`} />
                                            Light
                                        </button>
                                        <button
                                            onClick={() => { if (theme === 'light' && !useSystemDefault) toggleTheme(); }}
                                            disabled={useSystemDefault}
                                            className={`px-4 py-1.5 text-sm transition-all duration-300 flex items-center gap-1 rounded-md ${
                                                theme === 'dark' && !useSystemDefault ? 'card-container rounded-md' : 'bg-transparent text-font-secondary'
                                            }`}
                                        >
                                            <Icon icon="mdi:weather-night" className={`text-lg ${theme === 'dark' && !useSystemDefault ? 'text-primary' : 'text-font-secondary'}`} />
                                            Dark
                                        </button>
                                    </div>
                                    <div className="flex flex-row items-center gap-2">
                                        <input 
                                            type="checkbox" 
                                            id="systemDefault"
                                            checked={useSystemDefault}
                                            onChange={(e) => setSystemDefaultEnabled(e.target.checked)}
                                            className="w-4 h-4" 
                                        />
                                        <label htmlFor="systemDefault" className="text-sm font-semibold">
                                            System Default
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <hr className="border-b-2 border-bgAccent" />

                            <div className="flex flex-row justify-between items-center gap-4">
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-base">Default Date Range</span>
                                    <span className="text-sm text-font-secondary">Set the default number of days to display when loading charts and data</span>
                                </div>
                                <div className="flex flex-row items-center gap-3">
                                    <input 
                                        type="number" 
                                        min="1" 
                                        max="14" 
                                        value={getDefaultDateRangeDays()}
                                        onChange={handleDefaultDateRangeDaysChange}
                                        className="w-20 p-2 text-font-primary button-container bg-background rounded-md text-center focus:outline-none"
                                    />
                                    <span className="text-sm text-font-secondary">days (max 14)</span>
                                </div>
                            </div>

                            <hr className="border-b-2 border-bgAccent" />

                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                                <div className="flex flex-col gap-1">
                                    <label className="font-semibold text-base">Default Page</label>
                                    <span className="text-sm text-font-secondary">Choose which page to open when you log in</span>
                                </div>
                                <select 
                                    className="p-3 text-font-primary button-container bg-background w-full md:w-1/2 rounded-md focus:outline-none"
                                    value={preferences.defaultPage || ''}
                                    onChange={handleDefaultPageChange}
                                >
                                    <option value="">
                                        {currentDefaultLabel 
                                            ? `Select a page (Default: ${currentDefaultLabel})`
                                            : 'Select a default page...'
                                        }
                                    </option>
                                    {defaultPageOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                );
            
            case 'security':
                return (
                    <div className="flex flex-col gap-3">
                        <h3 className="subheader-textbox gap-1">
                            {/* <Icon icon="mdi:shield-account" className="responsive-icon-sm text-primary" /> */}
                            Security
                        </h3>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-row items-center justify-between gap-2">
                                <div className="flex flex-col items-start justify-between gap-2">
                                    <span className="font-semibold text-base">Password</span>
                                    <p className="text-sm text-font-secondary">
                                        Reset your password to improve account security
                                        <span className="text-sm font-light text-font-primary italic"> - Managed by Auth0</span>
                                    </p>
                                </div>
                                <button
                                    onClick={handlePasswordReset}
                                    className="px-3 py-2 text-font-primary button-container bg-background rounded-md text-sm text-background flex items-center gap-2"
                                >
                                    <Icon icon="mdi:lock-reset" className="text-lg text-error" />
                                    Reset Password
                                </button>
                            </div>

                            <hr className="border-b-2 border-bgAccent" />

                            <div className="flex flex-row items-center justify-between gap-2">
                                <div className="flex flex-col items-start justify-between gap-2">
                                    <span className="font-semibold text-base">Active Sessions</span>
                                    <p className="text-sm text-font-secondary">
                                        Current session: {new Date().toLocaleString()}
                                        <span className="text-sm text-font-primary font-light italic"> - 1 session</span>
                                    </p>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="px-3 py-2 text-font-primary button-container bg-background rounded-md text-sm text-background flex items-center gap-2"
                                >
                                    <Icon icon="mdi:logout" className="text-lg text-error" />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    </div>
                );
            
            case 'notifications':
                return (
                    <div className="flex flex-col gap-3">
                        <h3 className="subheader-textbox gap-1">
                            {/* <Icon icon="mdi:bell" className="responsive-icon-sm text-primary" /> */}
                            Notifications
                        </h3>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-row items-center justify-between">
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-base">Email Notifications</span>
                                    <span className="text-sm text-font-secondary">Receive alerts via email</span>
                                </div>
                                <input type="checkbox" className="w-4 h-4" defaultChecked />
                            </div>

                            <hr className="border-b-2 border-bgAccent" />

                            <div className="flex flex-row items-center justify-between">
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-base">System Alerts</span>
                                    <span className="text-sm text-font-secondary">Get notified about system issues</span>
                                </div>
                                <input type="checkbox" className="w-4 h-4" defaultChecked />
                            </div>

                            <hr className="border-b-2 border-bgAccent" />

                            <div className="flex flex-row items-center justify-between">
                                <div className="flex flex-col gap-1">
                                    <span className="font-semibold text-base">Call Alerts</span>
                                    <span className="text-sm text-font-secondary">Notifications for failed calls</span>
                                </div>
                                <input type="checkbox" className="w-4 h-4" defaultChecked />
                            </div>
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };

    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays - customize timing as needed
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, tabs: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, content: true }));
        }, 500));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Listen for page reset events from navbar
    useEffect(() => {
        const handlePageReset = (event) => {
            // Check if this page needs to reset based on its current state
            // If we're not on the profile tab, reset to profile
            if (activeTab !== 'profile') {
                setActiveTab('profile');
            }
        };

        window.addEventListener('pageReset', handlePageReset);

        return () => {
            window.removeEventListener('pageReset', handlePageReset);
        };
    }, [activeTab]);

    return (
        <div className="responsive-page-layout scrollable w-full gap-4">
            <div className={`flex flex-row justify-between items-center w-full transition-all duration-700 ease-out-in ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <div className="header-textbox">
                    <Icon icon="material-symbols:settings-rounded" className="header-icon" />
                    User Settings
                </div>
            </div>

            <div className={`flex flex-col gap-4 h-full w-full`}>
                {/* Tabs */}
                <div className={`w-full flex flex-row flex-nowrap gap-2 transition-all duration-1000 ease-out-in border-b border-border ${
                    animationStates.tabs
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                flex flex-row items-center justify-center gap-2 p-3 w-full text-left transition-colors
                                ${activeTab === tab.id
                                    ? 'tickets-tab-active'
                                    : 'tickets-tab-inactive hover:tag-primary'
                                }
                            `}
                        >
                            <Icon icon={tab.icon} className={`
                                text-xl
                                ${activeTab === tab.id
                                    ? 'text-primary'
                                    : 'text-font-secondary'
                                }`} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className={`flex-1 px-2 transition-all duration-1000 ease-out-in ${
                    animationStates.content
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

export default UserSettings;
